import React, { useState, useMemo } from 'react';
import { RevenueCalculatorProps } from 'src/app/molecules/RevenueCalculator/RevenueCalculator';
import Typography from 'src/app/atoms/Typography/Typography';
import Stack from 'src/app/atoms/Layout/Stack/Stack';
import useOnMobile from 'src/hooks/useOnMobile';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import { logStatsigEvent } from 'src/app/atoms/AbTesting/statsigClient';

type PlanType = 'launch' | 'scale' | 'growth';

interface PlanDetails {
  value: string;
  label: string;
  price: number;
  features: string[];
  defaultMembers: number;
  helperText: string;
}

const LAUNCH_FEATURES_LEGACY = [
  'The features you need to create a $48/mo offer',
  'AI Cohost and Community Design™ to support you',
  '2 months free when you buy annually',
];

const LAUNCH_FEATURES = [
  'Create a community with courses and events',
  'AI Cohost and Community Design™ to support you',
  '2 months free when you buy annually',
];

const LAUNCH_HELPER_TEXT_LEGACY =
  '*Launch Plans generate an average of $4,800/year. This is a representative offer. No guarantee of results.';

const LAUNCH_HELPER_TEXT =
  '*Launch Plans generate an average of $14,400/year. This is a representative offer. No guarantee of results.';

const PLAN_DETAILS: Record<PlanType, PlanDetails> = {
  launch: {
    value: 'launch',
    label: 'Launch Plan',
    price: 950,
    features: LAUNCH_FEATURES_LEGACY,
    defaultMembers: 10,
    helperText: LAUNCH_HELPER_TEXT_LEGACY,
  },
  scale: {
    value: 'scale',
    label: 'Scale Plan',
    price: 2150,
    features: [
      'Automations, Admin Features, and APIs for scaling',
      'AI Cohost and Community Design™ tuned for scale',
      '2 months free when you buy annually',
    ],
    defaultMembers: 50,
    helperText:
      '*Scale Plans generate an average of $24,000/year. This is a representative offer. No guarantee of results.',
  },
  growth: {
    value: 'growth',
    label: 'Growth Plan',
    price: 4250,
    features: [
      'Automations, Admin Features, and AI for Growth',
      'AI Cohost and Community Design™ tuned for Growth',
      'Billed annually',
    ],
    defaultMembers: 100,
    helperText:
      '*Growth Plans generate an average of $48,000/year. This is a representative offer. No guarantee of results.',
  },
};

interface DefaultValues {
  monthlyCharge?: number;
  members?: number;
  selectedPlan?: PlanType;
}

interface UseStandardPlansRevenueCalculatorControllerParams {
  defaults?: DefaultValues;
}

interface ExtendedRevenueCalculatorProps extends RevenueCalculatorProps {
  selectedPlan: PlanType;
}

const useStandardPlansRevenueCalculatorController = (
  params?: UseStandardPlansRevenueCalculatorControllerParams
): ExtendedRevenueCalculatorProps => {
  const {
    monthlyCharge: defaultMonthlyCharge = 48,
    members: defaultMembers,
    selectedPlan: defaultSelectedPlan = 'launch',
  } = params?.defaults || {};

  const isUnlimitedSpaces = useFeatureFlag('unlimited_spaces');
  const resolvedDefaultMembers = defaultMembers ?? (isUnlimitedSpaces ? 25 : 10);

  const [monthlyCharge, setMonthlyCharge] = useState(defaultMonthlyCharge);
  const [members, setMembers] = useState(resolvedDefaultMembers);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(defaultSelectedPlan);

  const isLessThan1280 = useOnMobile(1280);
  const isLessThan640 = useOnMobile(640);

  const formatThousandComma = (value: number) => {
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatCurrency = (value: number) => {
    return `$${formatThousandComma(value)}`;
  };

  const annualCharge = monthlyCharge * 10;
  const annualRevenue = isUnlimitedSpaces
    ? members * monthlyCharge * 12
    : members * annualCharge;
  const planPrice = PLAN_DETAILS[selectedPlan].price;
  const roi = (annualRevenue - planPrice) / planPrice;
  const makeBackPeriodMonths = Math.ceil(planPrice / (annualRevenue / 12));

  const shouldShowBasedOnContent = annualCharge <= 1000;
  const shouldHideForResponsive = isLessThan1280 && !isLessThan640;
  const showBadge = shouldShowBasedOnContent && !shouldHideForResponsive;

  const launchFeatures = isUnlimitedSpaces ? LAUNCH_FEATURES : LAUNCH_FEATURES_LEGACY;
  const launchHelperText = isUnlimitedSpaces ? LAUNCH_HELPER_TEXT : LAUNCH_HELPER_TEXT_LEGACY;

  const currentFeatures =
    selectedPlan === 'launch' ? launchFeatures : PLAN_DETAILS[selectedPlan].features;
  const currentHelperText =
    selectedPlan === 'launch' ? launchHelperText : PLAN_DETAILS[selectedPlan].helperText;

  const calculatorProps: RevenueCalculatorProps = useMemo(
    () => ({
      helperText: {
        text: currentHelperText,
        type: 'paragraph',
        size: 'xs',
        color: 'text-primary',
      },
      resultsBlock: {
        title: 'your revenue potential',
        value: formatCurrency(annualRevenue),
        subtitle: `Annual revenue from ${members} members at $${monthlyCharge}/month*`,
        bottomLeftLabel: 'Your ROI',
        bottomLeftValue: `${roi.toFixed(1)}x`,
        bottomLeftHint: 'Return on Investment calculation based on your annual revenue',
        bottomRightLabel: 'Make Back Period',
        bottomRightValue: `${makeBackPeriodMonths} ${makeBackPeriodMonths === 1 ? 'month' : 'months'}`,
        bottomRightHint: 'Time needed to recover your initial investment',
      },
      inputsBlock: {
        inputs: [
          {
            label: 'What you can charge monthly',
            value: monthlyCharge.toString(),
            money: true,
            type: 'number',
            max: 10000,
            min: 1,
            onChange: (value) => setMonthlyCharge(Number(value)),
          },
          {
            label: 'What you can charge annually',
            value: annualCharge.toString(),
            money: true,
            type: 'number',
            disabled: true,
            rightBadge: showBadge
              ? {
                  label: 'offer 2 months free',
                  color: 'brand',
                  noWrap: true,
                }
              : undefined,
            hint: !shouldShowBasedOnContent ? 'Offer 2 months free' : undefined,
          },
          {
            label: 'Your number of members',
            value: members.toString(),
            type: 'number',
            max: 100000,
            min: 1,
            rightPlaceholder: 'Members',
            onChange: (value) => setMembers(Number(value)),
          },
          {
            label: 'Investment',
            value: selectedPlan,
            type: 'select',
            options: [
              { value: 'launch', label: 'Launch Plan' },
              { value: 'scale', label: 'Scale Plan' },
              { value: 'growth', label: 'Growth Plan' },
            ],
            onChange: (value) => {
              const newPlan = value as PlanType;
              setSelectedPlan(newPlan);
              const defaultMembersForPlan =
                newPlan === 'launch' && isUnlimitedSpaces ? 25 : PLAN_DETAILS[newPlan].defaultMembers;
              setMembers(defaultMembersForPlan);
            },
          },
        ],
        button:
          selectedPlan === 'growth'
            ? {
                variant: 'primary',
                size: 'xl',
                children: 'Book a Sales Call',
                href: '/request-a-call-form',
                onClick: () => logStatsigEvent('revenue_calculator_book_a_sales_call', 1),
              }
            : {
                variant: 'primary',
                size: 'xl',
                children: 'Start Your Free Trial',
                href: 'https://cohost.mn.co/?plan=free-trial',
                onClick: () => logStatsigEvent('revenue_calculator_start_free_trial', 1),
              },
        listGroupItem: {
          borderColor: 'border-brand',
          heading: {
            text: PLAN_DETAILS[selectedPlan].label,
            type: 'paragraph',
            size: 'lg',
            fontWeight: 'font-weight-semibold',
            color: 'text-primary',
          },
          checkItemTextListProps: {
            checkItems: currentFeatures,
            checkItemProps: {
              type: 'simpleCheck',
              size: 'md',
              color: 'success',
              textColor: 'text-primary',
            },
            gap: 'spacing-lg',
          },
          topRightElement: (
            <Stack
              direction='row'
              gap='spacing-xs'
              align='center'
            >
              <Typography
                text={formatCurrency(PLAN_DETAILS[selectedPlan].price)}
                type='paragraph'
                size='xl'
                fontWeight='font-weight-semibold'
                color='text-primary'
              />
              <Typography
                text='/year'
                type='paragraph'
                size='sm'
                color='text-tertiary'
                as='span'
              />
            </Stack>
          ),
        },
      },
    }),
    [
      monthlyCharge,
      members,
      selectedPlan,
      annualCharge,
      annualRevenue,
      roi,
      makeBackPeriodMonths,
      planPrice,
      showBadge,
      shouldShowBasedOnContent,
      isLessThan1280,
      isLessThan640,
      currentFeatures,
      currentHelperText,
    ]
  );

  return {
    ...calculatorProps,
    selectedPlan,
  };
};

export default useStandardPlansRevenueCalculatorController;
